define("shared/utils/media-adjustments/load-media-adjustments", ["exports", "shared/utils/media-adjustments/apply-media-adjustments", "shared/utils/media-adjustments/build-media-spec-params", "shared/utils/media-adjustments/fetch-by-media-item", "shared/utils/media-adjustments/media-adjustments-routing", "shared/utils/media-adjustments/normalize", "shared/utils/media-adjustments/request-cache"], function (_exports, _applyMediaAdjustments, _buildMediaSpecParams, _fetchByMediaItem, _mediaAdjustmentsRouting, _normalize, _requestCache) {
  "use strict";

  Object.defineProperty(_exports, "__esModule", {
    value: true
  });
  _exports.loadMediaAdjustments = loadMediaAdjustments;
  _exports.requestMediaAdjustmentsPreview = requestMediaAdjustmentsPreview;
  0; //eaimeta@70e063a35619d71f0,"shared/utils/media-adjustments/apply-media-adjustments",0,"shared/utils/media-adjustments/build-media-spec-params",0,"shared/utils/media-adjustments/fetch-by-media-item",0,"shared/utils/media-adjustments/media-adjustments-routing",0,"shared/utils/media-adjustments/normalize",0,"shared/utils/media-adjustments/request-cache"eaimeta@70e063a35619d71f
  function mediaSpecCacheParams(spec) {
    if (!spec) {
      return undefined;
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const {
      media_item_id,
      ...params
    } = spec;
    return params;
  }

  /**
   * A stable signature of the media spec (crop, dimensions, file size, format, platform) a request
   * was issued for. Used to drop responses that resolve after the user has changed the media (e.g. a
   * pre-crop preview prefetch landing after a crop), which would otherwise resurrect stale violations.
   */
  function mediaSpecSignature(postMediaItem, post) {
    const params = mediaSpecCacheParams((0, _buildMediaSpecParams.buildMediaSpecParams)(postMediaItem, post));
    return params ? (0, _requestCache.stableParamsKey)(params) : '';
  }
  function postMediaItemCacheKey(postMediaItem, post) {
    return (0, _requestCache.buildPostMediaItemAdjustmentsCacheKey)(postMediaItem.id, mediaSpecCacheParams((0, _buildMediaSpecParams.buildMediaSpecParams)(postMediaItem, post)));
  }
  function invalidatePostMediaItemAdjustmentsCache(postMediaItem, post) {
    (0, _requestCache.invalidateMediaAdjustmentsCacheKey)(postMediaItemCacheKey(postMediaItem, post));
  }

  /**
   * Runs an async media-adjustments request, then commits its response only if the PMI's media spec
   * is unchanged since the request was issued. If the spec changed mid-flight (e.g. a crop landed
   * while a pre-crop request was in flight), the response is discarded and `onStale` is returned so
   * the request cannot resurrect stale violations.
   */
  async function withFreshSpec(postMediaItem, post, fetchResponse, commit, onStale) {
    const requestedSignature = mediaSpecSignature(postMediaItem, post);
    const response = await fetchResponse();
    if (mediaSpecSignature(postMediaItem, post) !== requestedSignature) {
      return onStale();
    }
    return commit(response);
  }
  function fetchPersistedMediaAdjustments(postMediaItem, post, options) {
    const cacheKey = postMediaItemCacheKey(postMediaItem, post);
    return withFreshSpec(postMediaItem, post, () => (0, _requestCache.fetchWithCache)(cacheKey, () => postMediaItem.fetchMediaAdjustments(), options), response => applyMediaAdjustments(postMediaItem, (0, _normalize.normalizeMediaAdjustments)(response.media_adjustments)), () => postMediaItem.mediaAdjustments ?? {
      platforms: {}
    });
  }
  function fetchPersistedMediaAdjustmentsPreview(postMediaItem, post, platform, platformKey) {
    invalidatePostMediaItemAdjustmentsCache(postMediaItem, post);
    return withFreshSpec(postMediaItem, post, () => postMediaItem.requestPreview({
      platform
    }), response => applyPreviewRequestResponse(postMediaItem, response, platformKey), () => postMediaItem.mediaAdjustments ?? null);
  }
  function applyMediaAdjustments(postMediaItem, payload) {
    let preserveResolvedViolations = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : false;
    return (0, _applyMediaAdjustments.applyMediaAdjustmentsToRecord)(postMediaItem, payload, {
      preserveResolvedViolations
    });
  }
  function applyPreviewRequestResponse(postMediaItem, response, platformKey) {
    const raw = response?.media_adjustments;
    if (raw && Object.keys(raw).length > 0) {
      return applyMediaAdjustments(postMediaItem, (0, _normalize.normalizeMediaAdjustments)(raw), true);
    }
    const existing = postMediaItem.mediaAdjustments;
    if (!existing || !platformKey || !existing.platforms[platformKey]) {
      return existing ?? null;
    }
    return applyMediaAdjustments(postMediaItem, {
      platforms: {
        ...existing.platforms,
        [platformKey]: {
          ...existing.platforms[platformKey],
          preview: {
            status: 'processing'
          }
        }
      }
    });
  }
  async function loadMediaAdjustments(postMediaItem, post, options) {
    const source = (0, _mediaAdjustmentsRouting.resolveMediaAdjustmentsSource)(postMediaItem, post);
    if (source.kind === 'unavailable') {
      return null;
    }
    if (source.kind === 'persisted') {
      return fetchPersistedMediaAdjustments(postMediaItem, post, options);
    }
    return withFreshSpec(postMediaItem, post, () => (0, _fetchByMediaItem.fetchMediaAdjustmentsByMediaItem)(source.mediaItemId, source.requestParams, options), response => applyMediaAdjustments(postMediaItem, (0, _normalize.normalizeMediaAdjustments)(response.media_adjustments)), () => postMediaItem.mediaAdjustments ?? null);
  }
  async function requestMediaAdjustmentsPreview(postMediaItem, post, platform) {
    const platformKey = platform ?? (0, _buildMediaSpecParams.buildMediaSpecParams)(postMediaItem, post)?.platform;
    const source = (0, _mediaAdjustmentsRouting.resolveMediaAdjustmentsSource)(postMediaItem, post);
    if (source.kind === 'unavailable') {
      return null;
    }
    if (source.kind === 'persisted') {
      return fetchPersistedMediaAdjustmentsPreview(postMediaItem, post, platform, platformKey);
    }
    return withFreshSpec(postMediaItem, post, () => (0, _fetchByMediaItem.requestMediaAdjustmentsPreviewByMediaItem)(source.mediaItemId, {
      ...source.requestParams,
      platform: platformKey ?? source.requestParams.platform
    }), response => applyPreviewRequestResponse(postMediaItem, response, platformKey), () => postMediaItem.mediaAdjustments ?? null);
  }
});